import { convertToNumberKWD } from './common';

/**
 * Transform catering packages data into the API payload format for add-to-cart
 * @param {Object} params - Parameters for the transformation
 * @param {number} params.customerId - Customer ID
 * @param {number} params.companyId - Company ID
 * @param {number} params.addressId - Address ID
 * @param {Array} params.packages - Array of catering packages
 * @param {Object} params.filters - Filters containing pax, date, time
 * @param {string} params.notes - Special instructions
 * @param {number} params.restaurantId - Restaurant ID
 * @param {number} params.branchId - Branch ID (default 0 for catering)
 * @param {number} params.countryId - Country ID (default 3 for Kuwait)
 * @returns {Object} API payload for catering add-to-cart
 */
export const transformCateringCartPayload = ({
  customerId,
  companyId,
  addressId,
  packages,
  filters,
  notes,
  restaurantId,
  branchId = 0,
  countryId = 3
}) => {
  // Get the first package to extract catering_id and calculate total price
  const firstPackage = packages[0];
  if (!firstPackage) {
    throw new Error('At least one package is required');
  }

  // Calculate total catering price across all packages
  const totalPrice = packages.reduce((total, pkg) => {
    const packagePrice = calculatePackagePrice(pkg, filters.pax);
    return total + packagePrice;
  }, 0);
  
  // Add chargeable items cost
  const chargeableItemsCost = calculateTotalChargeableItemsCost(packages, filters.pax);
  const totalPriceWithChargeableItems = totalPrice + chargeableItemsCost;

  // Transform items to the new catering service items format
  const cateringServiceItems = packages.flatMap(pkg => {
    if (!pkg.items || !Array.isArray(pkg.items)) return [];
    
    // Build a map of items by category to track order and determine which are chargeable
    const itemsByCategory = {};
    const itemDetails = []; // Store item details with their position
    
    pkg.items.forEach(item => {
      const categoryId = item.category_id || item.catering_category_id || "1";
      
      if (!itemsByCategory[categoryId]) {
        itemsByCategory[categoryId] = [];
      }
      
      if (item.has_variants && item.items) {
        item.items.forEach(variantItem => {
          const addOrderHistory = variantItem.addOrderHistory || 
            Array.from({ length: variantItem.quantity || 0 }, (_, i) => i);
          
          addOrderHistory.forEach(order => {
            const itemPrice = calculateVariantItemPrice(item, variantItem);
            itemsByCategory[categoryId].push({ order, price: itemPrice });
            itemDetails.push({
              categoryId,
              item,
              variantItem,
              order,
              price: itemPrice,
              isVariant: true
            });
          });
        });
      } else {
        const addOrderHistory = item.addOrderHistory || 
          Array.from({ length: item.quantity || 0 }, (_, i) => i);
        
        addOrderHistory.forEach(order => {
          const itemPrice = getCateringItemPrice(item, filters.pax);
          itemsByCategory[categoryId].push({ order, price: itemPrice });
          itemDetails.push({
            categoryId,
            item,
            order,
            price: itemPrice,
            isVariant: false
          });
        });
      }
    });
    
    // For each category, determine which items are chargeable
    const chargeableMap = {}; // Map to track which items at which order positions are chargeable
    
    Object.keys(itemsByCategory).forEach(categoryId => {
      const categoryItems = itemsByCategory[categoryId];
      const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));
      
      // Separate matrix-based items from standalone/add-on items
      // Standalone and add-on items are always chargeable, not subject to category limits
      const matrixBasedItems = [];
      const standaloneOrAddOnItems = [];
      
      categoryItems.forEach((categoryItem, index) => {
        const itemDetail = itemDetails.find(detail => detail.order === categoryItem.order);
        if (itemDetail) {
          const effectiveAddOnType = itemDetail.item.add_on_type;
          const isStandaloneOrAddOn = effectiveAddOnType || itemDetail.item.price_type === "Standalone Item Based";
          
          if (isStandaloneOrAddOn) {
            standaloneOrAddOnItems.push(categoryItem);
          } else {
            matrixBasedItems.push(categoryItem);
          }
        } else {
          // If we can't find the item detail, assume it's matrix-based (safe default)
          matrixBasedItems.push(categoryItem);
        }
      });
      
      let maxFreeItems = 0;
      if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
        const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingRule = sortedRules.find(rule => filters.pax >= parseInt(rule.pax));
        
        if (matchingRule && matchingRule.choose > 0) {
          maxFreeItems = matchingRule.choose;
        }
      }
      
      // Sort matrix-based items by order (temporal sequence)
      matrixBasedItems.sort((a, b) => a.order - b.order);
      
      // For matrix-based items: Items beyond maxFreeItems are chargeable
      const chargeableMatrixOrders = matrixBasedItems.slice(maxFreeItems).map(item => item.order);
      
      // All standalone/add-on items are chargeable
      const chargeableStandaloneOrders = standaloneOrAddOnItems.map(item => item.order);
      
      // Combine both sets
      chargeableMap[categoryId] = new Set([...chargeableMatrixOrders, ...chargeableStandaloneOrders]);
    });
    
    // Now create the API payload items
    // Group items by ID and add_on_type to send separate objects for each variation
    const groupedItems = {};
    
    pkg.items.forEach(item => {
      const categoryId = item.category_id || item.catering_category_id || "1";
      const itemId = item.id.toString();
      const addOnType = item.add_on_type || ""; // Empty string for normal items
      
      // Create unique key for grouping: itemId + addOnType
      const groupKey = `${itemId}_${addOnType}`;
      
      if (!groupedItems[groupKey]) {
        groupedItems[groupKey] = {
          item: item,
          categoryId: categoryId,
          itemId: itemId,
          addOnType: addOnType,
          quantity: 0,
          addOrderHistory: []
        };
      }
      
      // Accumulate quantity and order history
      if (item.has_variants && item.items) {
        // For variant items
        item.items.forEach(variantItem => {
          groupedItems[groupKey].quantity += variantItem.quantity || 0;
          const variantOrderHistory = variantItem.addOrderHistory || 
            Array.from({ length: variantItem.quantity || 0 }, (_, i) => i);
          groupedItems[groupKey].addOrderHistory.push(...variantOrderHistory);
        });
      } else {
        // For non-variant items
        groupedItems[groupKey].quantity = item.quantity || 0;
        groupedItems[groupKey].addOrderHistory = item.addOrderHistory || 
          Array.from({ length: item.quantity || 0 }, (_, i) => i);
      }
    });
    
    // Now create payload from grouped items
    return Object.values(groupedItems).map(groupedItem => {
      const { item, categoryId, itemId, addOnType, quantity, addOrderHistory } = groupedItem;
      
      // Find the category limit to get the correct "choose" value
      const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));
      let chooseValue = "1"; // Default fallback
      
      if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
        const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingRule = sortedRules.find(rule => 
          filters.pax >= parseInt(rule.pax)
        );
        
        if (matchingRule && matchingRule.choose > 0) {
          chooseValue = matchingRule.choose.toString();
        }
      }
      
      // Determine portion value based on add_on_type or PAX
      const getPortionForPax = (item, currentPax, addOnType) => {
        // PRIORITY 1: If item has add_on_type (half/full), get portion from add_on_options
        if (addOnType && item.add_on_options && Array.isArray(item.add_on_options)) {
          const matchingAddOn = item.add_on_options.find(opt => opt.add_on_type === addOnType);
          if (matchingAddOn && matchingAddOn.portion) {
            return matchingAddOn.portion.toString();
          }
        }
        
        // PRIORITY 2: Otherwise, use portion_by_pax based on PAX
        if (item.portion_by_pax && Array.isArray(item.portion_by_pax) && item.portion_by_pax.length > 0) {
          const sortedPortions = [...item.portion_by_pax].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
          const matchingPortion = sortedPortions.find(p => currentPax >= parseInt(p.pax));
          
          if (matchingPortion && matchingPortion.portions) {
            return matchingPortion.portions.toString();
          }
          
          if (item.portion_by_pax[0] && item.portion_by_pax[0].portions) {
            return item.portion_by_pax[0].portions.toString();
          }
        }
        
        // Default: return "1" as a reasonable fallback
        return "1";
      };
      
      const portionValue = getPortionForPax(item, filters.pax, addOnType);
      
      // Calculate item price (use add_on_type if present)
      // If item is complimentary, set price to 0
      const itemPrice = item.isComplimentary ? 0 : (item.has_variants 
        ? 0 // Will be calculated from variant items
        : getCateringItemPrice(item, filters.pax, addOnType || null));
      
      // Calculate add_on_price: sum of prices for chargeable instances only
      let totalAddOnPrice = 0;
      addOrderHistory.forEach(order => {
        if (chargeableMap[categoryId] && chargeableMap[categoryId].has(order)) {
          totalAddOnPrice += itemPrice;
        }
      });
      
      // Determine if this item is complementary
      // An item is complementary if it has a complimentaryCount > 0
      const isComplementary = (item.complimentaryCount || 0) > 0;
      
      const payloadItem = {
        catering_category_id: categoryId.toString(),
        catering_item_id: itemId,
        quantity: quantity.toString(),
        choose: chooseValue,
        portion: portionValue,
        no_of_pax: filters.pax?.toString() || "20",
        add_on_price: totalAddOnPrice > 0 ? totalAddOnPrice.toFixed(3) : "",
        add_on_type: addOnType,
        currency: "KWD",
        is_complementary: isComplementary
      };
      
      return payloadItem;
    });
  }).flat();

  // Format date to DD-MM-YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-GB');
    
    try {
      // Check if the date is already in DD-MM-YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
        return dateString;
      }
      
      // Handle YYYY-MM-DD format (from date input)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
      }
      
      // Handle other formats by parsing as Date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
      
      // If all else fails, return today's date
      console.warn('Unable to format date:', dateString);
      return new Date().toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Error formatting date:', error, 'Date string:', dateString);
      return new Date().toLocaleDateString('en-GB');
    }
  };

  // Parse time slot from filters
  const timeSlot = filters.time || "02:00 PM - 03:30 PM";

  return {
    country_id: countryId,
    catering_id: firstPackage.id,
    restaurant_id: restaurantId,
    customer_id: customerId,
    company_id: companyId,
    address_id: addressId,
    no_of_pax: filters.pax,
    quantity: 1, // Default quantity for the catering service
    service_item_price: totalPriceWithChargeableItems.toString(),
    currency: "KWD",
    service_item_date: formatDate(filters.date),
    service_item_slot: timeSlot,
    is_discount_applied: "",
    promo_code_type: "",
    promo_code: "",
    discount_price: "",
    special_instructions_1: notes || "",
    special_instructions_2: "", // Additional instructions if needed
    catering_service_items: cateringServiceItems
  };
};

/**
 * Transform backend cart data to frontend structure for checkoutCart
 * @param {Object} backendCartData - Cart data from backend API
 * @returns {Array} Array of packages in frontend format
 */
export const transformBackendCartToFrontend = (backendCartData) => {
  if (!backendCartData || !Array.isArray(backendCartData)) {
    return [];
  }

  return backendCartData.map(cartItem => {
    // Transform backend cart item to frontend package structure
    const packageData = {
      id: cartItem.catering_id || cartItem.id,
      name_en: cartItem.package_name_en || cartItem.name_en,
      title: cartItem.package_name_en || cartItem.name_en,
      description_en: cartItem.package_description_en || cartItem.description_en,
      image1: cartItem.package_image || cartItem.image,
      basePrice: parseFloat(cartItem.catering_price?.replace('KWD ', '') || '0'),
      no_of_pax: cartItem.no_of_pax || 20,
      duration_from: cartItem.duration_from || "01:00 PM",
      duration_to: cartItem.duration_to || "04:00 PM",
      special_instructions: cartItem.special_instructions || "",
      items: []
    };

    // Transform catering items if they exist
    if (cartItem.catering_items && Array.isArray(cartItem.catering_items)) {
      packageData.items = cartItem.catering_items.map(item => {
        const transformedItem = {
          id: item.item_id,
          name_en: item.item_name_en || item.name_en,
          name: item.item_name_en || item.name_en,
          quantity: item.quantity || 1,
          price: item.item_price || "0.000",
          has_variants: false,
          items: [] // For variant items
        };

        // Check if item has variants
        if (item.size_variant && item.size_variant.length > 0 ||
            item.topping_variant && item.topping_variant.length > 0 ||
            item.custom_variant && item.custom_variant.length > 0 ||
            item.side_variant && item.side_variant.length > 0) {
          
          transformedItem.has_variants = true;
          
          // Create a variant item entry
          const variantItem = {
            variantId: `${item.item_id}_${Date.now()}`, // Generate unique variant ID
            quantity: item.quantity || 1,
            selectedVariant: []
          };

          // Add size variants
          if (item.size_variant && item.size_variant.length > 0) {
            variantItem.selectedVariant.push({
              variant_type: 'size',
              options: item.size_variant
            });
          }

          // Add topping variants
          if (item.topping_variant && item.topping_variant.length > 0) {
            variantItem.selectedVariant.push({
              variant_type: 'topping',
              options: item.topping_variant
            });
          }

          // Add custom variants
          if (item.custom_variant && item.custom_variant.length > 0) {
            variantItem.selectedVariant.push({
              variant_type: 'custom',
              options: item.custom_variant
            });
          }

          // Add side variants
          if (item.side_variant && item.side_variant.length > 0) {
            variantItem.selectedVariant.push({
              variant_type: 'side',
              options: item.side_variant
            });
          }

          transformedItem.items = [variantItem];
        }

        return transformedItem;
      });
    }

    return packageData;
  });
};

/**
 * Get the price of a catering item based on its add_on_options and current PAX
 * @param {Object} item - The catering item with add_on_options
 * @param {number} currentPax - Current PAX count
 * @param {string} addOnType - Optional add-on type ('half' or 'full')
 * @returns {number} Price for the current PAX and add-on type
 */
export const getCateringItemPrice = (item, currentPax, addOnType = null) => {
  // NEW: If this is an add-on item (half/full), it's always chargeable regardless of price_type
  const effectiveAddOnType = item.add_on_type || addOnType;
  

  // PRIORITY 1: Check price_type for standalone items first (when not an add-on)
  // This ensures standalone items show their price even if they have add_on_options
  if (!effectiveAddOnType && item.price_type) {
    if (item.price_type === "Standalone Item Based") {
      // Standalone items are always chargeable - return the item's price
      if (item.price) {
        // Use convertToNumberKWD to handle "KWD X.XXX" format
        const priceValue = convertToNumberKWD(item.price);
        console.log(`  ✅ Standalone item price: ${priceValue} (from price: ${item.price}, type: ${typeof item.price})`);
        return priceValue || 0;
      } else {
        console.log(`  ❌ Standalone item but no price field found`);
      }
    } else if (item.price_type === "Matrix Based") {
      // Matrix-based items are part of package with 0 price (when added normally)
      // They follow category requirements and are only chargeable when exceeding limits
      // If they have add_on_options with valid prices, those will be used when selected as half/full
      console.log(`  ✅ Matrix-based item (normal), returning 0 (follows category limits)`);
      return 0;
    }
  }
  
  // PRIORITY 2: Check if item has add_on_options (catering items)
  if (item.add_on_options && Array.isArray(item.add_on_options) && item.add_on_options.length > 0) {
    // Check for new structure with add_on_type (half/full)
    if (item.add_on_options[0].add_on_type !== undefined) {
      if (effectiveAddOnType) {
        // Find the matching add-on option by type
        const matchingOption = item.add_on_options.find(option => 
          option.add_on_type === effectiveAddOnType
        );
        
        if (matchingOption && matchingOption.price) {
          return parseFloat(matchingOption.price) || 0;
        }
      }
      
      // If no add-on type specified and no price_type, return 0 (item requires add-on selection)
      return 0;
    }
    
    // Check for old structure with pax_from/pax_to
    if (item.add_on_options[0].pax_from !== undefined && item.add_on_options[0].pax_to !== undefined) {
      // Find the matching price tier based on current PAX
      const matchingOption = item.add_on_options.find(option => {
        const paxFrom = parseInt(option.pax_from || 0);
        const paxTo = parseInt(option.pax_to || 999);
        return currentPax >= paxFrom && currentPax <= paxTo;
      });

      if (matchingOption && matchingOption.price) {
        return parseFloat(matchingOption.price) || 0;
      }

      // Fallback to first option if no match
      if (item.add_on_options[0] && item.add_on_options[0].price) {
        return parseFloat(item.add_on_options[0].price) || 0;
      }
    }
  }

  // Fallback to item.price if it exists (old behavior for backwards compatibility)
  if (item.price) {
    return convertToNumberKWD(item.price);
  }

  return 0;
};

/**
 * Check if an item requires add-on selection (has new add_on_type structure with valid options)
 * @param {Object} item - The catering item
 * @returns {boolean} True if item requires add-on selection
 */
export const itemRequiresAddOnSelection = (item) => {
  if (!item || !item.add_on_options || !Array.isArray(item.add_on_options) || item.add_on_options.length === 0) {
    return false;
  }
  
  // Check if first option has add_on_type field (new structure)
  const hasAddOnType = item.add_on_options[0].add_on_type !== undefined;
  
  if (!hasAddOnType) {
    return false;
  }
  
  // Check if at least one option has valid portion and price
  // Items with add_on_type but empty/invalid portion/price shouldn't trigger the modal
  const hasValidOption = item.add_on_options.some(option => {
    const hasValidPortion = option.portion && option.portion !== '' && option.portion !== '0';
    const hasValidPrice = option.price && parseFloat(option.price) > 0;
    return hasValidPortion && hasValidPrice;
  });
  
  // If no valid options, don't show modal regardless of price_type
  if (!hasValidOption) {
    return false;
  }
  
  // For Matrix Based items with valid add_on_options:
  // - They CAN have half/full selections when category limits are exceeded
  // - The shouldShowAddOnOptions function will determine WHEN to show the modal
  // - This function just checks IF the item CAN show the modal (has valid structure)
  return true;
};

/**
 * Check if item should show add-on options based on category limit
 * @param {Object} item - The catering item
 * @param {Array} cartItems - Current cart items
 * @param {Object} packageData - Package data with categories
 * @param {number} currentPax - Current PAX count
 * @returns {boolean} True if add-on options should be shown
 */
export const shouldShowAddOnOptions = (item, cartItems, packageData, currentPax) => {
  // If item doesn't have add-on options, return false
  if (!itemRequiresAddOnSelection(item)) {
    return false;
  }
  
  // Find the category limit for this item
  const categoryLimit = packageData?.categories?.find(category => category.id === item.category_id);
  
  if (!categoryLimit || !categoryLimit.choose_rule || categoryLimit.choose_rule.length === 0) {
    // No category limit, always show add-on options
    return true;
  }
  
  // Find the appropriate choose rule for current PAX
  const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
  const matchingRule = sortedRules.find(rule => currentPax >= parseInt(rule.pax));
  
  if (!matchingRule || matchingRule.choose <= 0) {
    // No matching rule, always show add-on options
    return true;
  }
  
  // Count current items in this category (excluding add-on items)
  const itemsInCategory = cartItems.filter(cartItem => 
    cartItem.category_id === item.category_id && !cartItem.add_on_type
  );
  
  // Calculate total quantity in category (non-add-on items only)
  const totalCategoryQuantity = itemsInCategory.reduce((total, cartItem) => {
    if (cartItem.has_variants && cartItem.items) {
      return total + cartItem.items.reduce((variantTotal, variantItem) => 
        variantTotal + (variantItem.quantity || 0), 0
      );
    } else {
      return total + (cartItem.quantity || 0);
    }
  }, 0);
  
  const maxFreeItems = matchingRule.choose;
  
  // If category limit is exceeded, show add-on options
  return totalCategoryQuantity >= maxFreeItems;
};

/**
 * Calculate variant item price including base price and options
 * @param {Object} item - The menu item object
 * @param {Object} variantItem - The variant item with selected options
 * @returns {number} Total price (base + options)
 */
export const calculateVariantItemPrice = (item, variantItem) => {
  // Get base price of the item
  const basePrice = convertToNumberKWD(item.price);

  // Check if selectedVariant exists and is an array
  if (!variantItem.selectedVariant || !Array.isArray(variantItem.selectedVariant) || variantItem.selectedVariant.length === 0) {
    console.warn('Invalid or empty selectedVariant for variant item:', {
      item: item.name || item.name_en,
      itemId: item.id,
      itemPrice: item.price,
      basePrice: basePrice,
      variantItem: variantItem,
      selectedVariant: variantItem.selectedVariant
    });
    // Return just the base price if no valid variant selections
    if (isNaN(basePrice)) {
      console.error('Base price is NaN for item:', item);
      return 0; // Ultimate fallback
    }
    return basePrice;
  }

  // Calculate options price
  const optionsPrice = variantItem.selectedVariant.reduce(
    (total, variant) => {
      // Check if variant has options array
      if (!variant.options || !Array.isArray(variant.options)) {
        console.warn('Variant missing options array:', variant);
        return total;
      }
      
      const optionsTotal = variant.options.reduce((optTotal, opt) => {
        return optTotal + (opt.unit_price || 0) * (opt.option_quantity || 0);
      }, 0);
      return total + optionsTotal;
    },
    0
  );

  // Return total price (base price + options price)
  return basePrice + optionsPrice;
};

/**
 * Calculate chargeable items based on pax_limit logic
 * @param {Array} items - Array of items to calculate
 * @param {number} currentPax - Current PAX count
 * @param {Array} paxLimits - Array of pax limit rules for categories
 * @returns {Array} Items with chargeable quantities calculated
 */
export const calculateChargeableItems = (items, currentPax, paxLimits) => {
  if (!Array.isArray(items) || !Array.isArray(paxLimits) || !currentPax) {
    return items || [];
  }

  return items.map(item => {
    // Find the pax limit for this item's category
    const categoryLimit = paxLimits.find(limit => limit.category_id === item.category_id);

    if (!categoryLimit || categoryLimit.item_limit === null || categoryLimit.pax_limit === null) {
      // No limit defined for this category, item is fully chargeable
      return { 
        ...item, 
        chargeableQuantity: item.quantity || 0,
        freeQuantity: 0,
        isChargeablePerItem: true // All items in this category are chargeable
      };
    }

    // Calculate how many items are free based on pax
    const maxFreePax = categoryLimit.pax_limit;
    const itemsPerPax = categoryLimit.item_limit;

    // Calculate free items: (current_pax / max_free_pax) * items_per_pax
    const freeItems = Math.floor((currentPax / maxFreePax) * itemsPerPax);

    // Chargeable quantity is total minus free items (minimum 0)
    const chargeableQuantity = Math.max(0, (item.quantity || 0) - freeItems);
    const freeQuantity = (item.quantity || 0) - chargeableQuantity;

    return {
      ...item,
      chargeableQuantity,
      freeQuantity,
      isChargeablePerItem: chargeableQuantity > 0, // Flag to show if this item has chargeable quantities
      categoryLimit // Store the limit info for display purposes
    };
  });
};

/**
 * Calculate display price for individual items based on pax limits
 * @param {Object} item - The menu item
 * @param {Array} paxLimits - Array of pax limit rules
 * @param {number} currentPax - Current PAX count
 * @param {Array} allItemsInCategory - All items in the same category for total calculation
 * @returns {number} Display price (0 if free, full price if chargeable)
 */
export const calculateItemDisplayPrice = (item, paxLimits, currentPax, allItemsInCategory = []) => {
  // Find the pax limit for this item's category
  const categoryLimit = paxLimits.find(limit => limit.category_id === item.category_id);
  
  if (!categoryLimit || categoryLimit.item_limit === null || categoryLimit.pax_limit === null) {
    // No limit defined for this category, show full price
    return getCateringItemPrice(item, currentPax);
  }

  // Calculate how many items are free based on pax
  const maxFreePax = categoryLimit.pax_limit;
  const itemsPerPax = categoryLimit.item_limit;
  const freeItems = Math.floor((currentPax / maxFreePax) * itemsPerPax);
  
  // Count total items in this category across all items in the package
  const itemsInSameCategory = allItemsInCategory.filter(itm => itm.category_id === item.category_id);
  const totalCategoryQuantity = itemsInSameCategory.reduce((sum, itm) => sum + (itm.quantity || 0), 0);
  
  // If all items in this category are within the free limit, show 0
  if (totalCategoryQuantity <= freeItems) {
    return 0;
  }
  
  // For simplicity, if any item in the category exceeds the limit, show full price for all
  // In a more complex implementation, you might want to track which specific items are free
  return getCateringItemPrice(item, currentPax);
};

/**
 * Calculate total chargeable items cost across all packages
 * @param {Array} packages - Array of packages with items
 * @param {number} currentPax - Current PAX count
 * @returns {number} Total cost of chargeable items
 */
export const calculateTotalChargeableItemsCost = (packages, currentPax) => {
  if (!packages || packages.length === 0) return 0;

  return packages.reduce((packageTotal, pkg) => {
    if (!pkg.items || !Array.isArray(pkg.items)) return packageTotal;

    // Separate items into two groups:
    // 1. Standalone items (always chargeable, ignore category limits)
    // 2. Matrix-based items (follow category limit rules)
    let standaloneItemsCost = 0;
    const matrixItemsByCategory = {};
    
    // First, organize items by their pricing type
    pkg.items.forEach(item => {
      const categoryId = item.category_id;
      const effectiveAddOnType = item.add_on_type;
      
      // Add-ons are always chargeable (treated like standalone)
      // Standalone items are always chargeable
      const isStandaloneOrAddOn = effectiveAddOnType || item.price_type === "Standalone Item Based";

      if (isStandaloneOrAddOn) {
        // Calculate cost for standalone/add-on items directly
        // Subtract complimentary count from quantity
        if (item?.has_variants) {
          item.items?.forEach(variantItem => {
            const variantPrice = calculateVariantItemPrice(item, variantItem);
            const quantity = variantItem.quantity || 0;
            const complimentaryCount = variantItem.complimentaryCount || 0;
            const chargeableQuantity = Math.max(0, quantity - complimentaryCount);
            const itemTotal = variantPrice * chargeableQuantity;
            standaloneItemsCost += itemTotal;
          });
        } else {
          const itemPrice = getCateringItemPrice(item, currentPax);
          const quantity = item.quantity || 0;
          const complimentaryCount = item.complimentaryCount || 0;
          const chargeableQuantity = Math.max(0, quantity - complimentaryCount);
          const itemTotal = itemPrice * chargeableQuantity;
          standaloneItemsCost += itemTotal;
        }
      } else {
        // Matrix-based items follow category limit rules
        if (!matrixItemsByCategory[categoryId]) {
          matrixItemsByCategory[categoryId] = [];
        }
        matrixItemsByCategory[categoryId].push(item);
      }
    });

    // Calculate cost for matrix-based items by category
    const matrixItemsCost = Object.keys(matrixItemsByCategory).reduce((categoryTotal, categoryId) => {
      const categoryItems = matrixItemsByCategory[categoryId];
      const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));
      
      let maxFreeItemsForCategory = 0;
      
      if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
        // Find the appropriate choose rule for current PAX
        // Sort rules by pax descending and find the first one where currentPax >= rule.pax
        const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingRule = sortedRules.find(rule => 
          currentPax >= parseInt(rule.pax)
        );
        
        if (matchingRule && matchingRule.choose > 0) {
          maxFreeItemsForCategory = matchingRule.choose;
        }
      }

      // Calculate total quantity in this category (only matrix-based items)
      let totalCategoryQuantity = 0;
      // Store item prices with their add order for proper temporal sorting
      const itemPricesWithOrder = [];
      categoryItems.forEach(item => {
        if (item?.has_variants) {
          // For variant items, sum up all variant quantities
          item.items?.forEach(variantItem => {
            totalCategoryQuantity += variantItem.quantity || 0;
            const variantPrice = calculateVariantItemPrice(item, variantItem);
            
            // If variant has addOrderHistory, use it; otherwise use sequential order
            const addOrderHistory = variantItem.addOrderHistory || 
              Array.from({ length: variantItem.quantity || 0 }, (_, i) => i);
            
            addOrderHistory.forEach(order => {
              itemPricesWithOrder.push({ price: variantPrice, order });
            });
          });
        } else {
          // For catering items (with add_on_options) or regular items
          totalCategoryQuantity += item.quantity || 0;
          // Use getCateringItemPrice which handles both add_on_options and regular price
          const itemPrice = getCateringItemPrice(item, currentPax);
          
          // Use addOrderHistory to track when each instance was added
          const addOrderHistory = item.addOrderHistory || 
            Array.from({ length: item.quantity || 0 }, (_, i) => i);
          
          addOrderHistory.forEach(order => {
            itemPricesWithOrder.push({ price: itemPrice, order });
          });
        }
      });
      
      // Sort by add order (temporal sequence) to maintain the actual addition order
      itemPricesWithOrder.sort((a, b) => a.order - b.order);
      const itemPrices = itemPricesWithOrder.map(item => item.price);

      // Calculate chargeable quantity for this category
      const chargeableCategoryQuantity = Math.max(0, totalCategoryQuantity - maxFreeItemsForCategory);

      if (chargeableCategoryQuantity === 0) {
        return categoryTotal; // All items in this category are free
      }

      // First N items (based on limit) are FREE, remaining items are CHARGEABLE
      // Charge for items that exceed the category limit in the order they were added
      const chargeablePrices = itemPrices.slice(maxFreeItemsForCategory);
      const categoryCost = chargeablePrices.reduce((sum, price) => sum + price, 0);

      return categoryTotal + categoryCost;
    }, 0);

    const totalPackageItemsCost = standaloneItemsCost + matrixItemsCost;

    return packageTotal + totalPackageItemsCost;
  }, 0);
};

/**
 * Calculate package price based on PAX count
 * @param {Object} pkg - Package object with pricing_tiers
 * @param {number} currentPax - Current PAX count
 * @returns {number} Package price for the current PAX count
 */
export const calculatePackagePrice = (pkg, currentPax) => {
  if (!pkg || !currentPax) return 0;

  // Check if package has new pricing_tiers structure (snake_case from API)
  if (pkg.pricing_tiers && Array.isArray(pkg.pricing_tiers)) {
    // Sort tiers by PAX in descending order to find the highest applicable tier
    const sortedTiers = [...pkg.pricing_tiers].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
    
    // Find the appropriate pricing tier for current PAX
    const matchingTier = sortedTiers.find(tier => 
      currentPax >= parseInt(tier.pax)
    );

    if (matchingTier && matchingTier.price) {
      return convertToNumberKWD(matchingTier.price);
    }

    // If no exact match, use the lowest tier (fallback to first tier)
    if (pkg.pricing_tiers.length > 0) {
      return convertToNumberKWD(pkg.pricing_tiers[0].price);
    }
  }

  // Also check for camelCase version (for backward compatibility)
  if (pkg.pricingTiers && Array.isArray(pkg.pricingTiers)) {
    // Sort tiers by PAX in descending order to find the highest applicable tier
    const sortedTiers = [...pkg.pricingTiers].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
    
    // Find the appropriate pricing tier for current PAX
    const matchingTier = sortedTiers.find(tier => 
      currentPax >= parseInt(tier.pax)
    );

    if (matchingTier && matchingTier.price) {
      return convertToNumberKWD(matchingTier.price);
    }

    // If no exact match, use the lowest tier (fallback to first tier)
    if (pkg.pricingTiers.length > 0) {
      return convertToNumberKWD(pkg.pricingTiers[0].price);
    }
  }

  // Fallback to old structure for backward compatibility
  const paxRanges = [
    {
      from: pkg.pax_range_from,
      to: pkg.pax_range_to,
      price: pkg.pax_price
    },
    {
      from: pkg.pax_range_from2,
      to: pkg.pax_range_to2,
      price: pkg.pax_price2
    },
    {
      from: pkg.pax_range_from3,
      to: pkg.pax_range_to3,
      price: pkg.pax_price3
    },
    {
      from: pkg.pax_range_from4,
      to: pkg.pax_range_to4,
      price: pkg.pax_price4
    },
    {
      from: pkg.pax_range_from5,
      to: pkg.pax_range_to5,
      price: pkg.pax_price5
    }
  ].filter(range => range.from && range.to && range.price); // Filter out empty ranges

  // Find the appropriate price range for current PAX
  for (const range of paxRanges) {
    if (currentPax >= range.from && currentPax <= range.to) {
      return convertToNumberKWD(range.price);
    }
  }

  // If no range matches, return 0 (or you could return the first range price as fallback)
  return 0;
};

/**
 * Calculate total package base prices
 * @param {Array} packages - Array of packages
 * @param {number} currentPax - Current PAX count
 * @returns {number} Total base price of all packages
 */
export const calculateTotalPackageBasePrice = (packages, currentPax) => {
  if (!packages || packages.length === 0) return 0;

  return packages.reduce((total, pkg) => {
    // First try the new PAX-based pricing
    const paxBasedPrice = calculatePackagePrice(pkg, currentPax);
    if (paxBasedPrice > 0) {
      return total + paxBasedPrice;
    }
    
    // Fallback to old basePrice field if it exists
    const basePrice = typeof pkg.basePrice === 'number' ? pkg.basePrice : 0;
    return total + basePrice;
  }, 0);
};

/**
 * Calculate total display price for a specific item/variant quantity in the context of its package and category
 * @param {Object} item - The menu item
 * @param {Object} variantItem - The variant item (if applicable)
 * @param {Object} pkg - The package containing this item
 * @param {number} currentPax - Current PAX count
 * @returns {number} Total display price for this item's quantity (includes both free and chargeable portions)
 */
export const calculateItemDisplayPriceInPackage = (item, variantItem, pkg, currentPax) => {
  // Use getCateringItemPrice for catering items, or calculateVariantItemPrice for variant items
  const unitPrice = variantItem ? calculateVariantItemPrice(item, variantItem) : getCateringItemPrice(item, currentPax);
  const itemQuantity = variantItem ? variantItem.quantity : item.quantity;
  
  // Calculate complimentary portion
  const complimentaryCount = item.complimentaryCount || (variantItem && variantItem.complimentaryCount) || 0;
  const chargeableQuantity = Math.max(0, itemQuantity - complimentaryCount);
  
  // NEW: Check if this is a standalone item or add-on (always chargeable, ignore category limits)
  const effectiveAddOnType = item.add_on_type;
  const isStandaloneOrAddOn = effectiveAddOnType || item.price_type === "Standalone Item Based";
  
  if (isStandaloneOrAddOn) {
    return unitPrice * chargeableQuantity;
  }
  
  // For matrix-based items, apply category limit logic
  const categoryLimit = pkg.categories?.find(category => category.id === item.category_id);
  
  if (!categoryLimit || !categoryLimit.choose_rule || categoryLimit.choose_rule.length === 0) {
    // No limit defined for this category, show full price (minus complimentary)
    return unitPrice * chargeableQuantity;
  }

  // Find the appropriate choose rule for current PAX
  // Sort rules by pax descending and find the first one where currentPax >= rule.pax
  const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
  const matchingRule = sortedRules.find(rule => 
    currentPax >= parseInt(rule.pax)
  );
  
  if (!matchingRule || matchingRule.choose <= 0) {
    // No matching rule or no item limit, show full price (minus complimentary)
    return unitPrice * chargeableQuantity;
  }

  // Build a list of all MATRIX-BASED items in this category using addOrderHistory for temporal ordering
  // Exclude standalone items and add-ons from category limit calculation
  const itemsInSameCategory = pkg.items?.filter(itm => 
    itm.category_id === item.category_id && 
    !itm.add_on_type && 
    itm.price_type !== "Standalone Item Based"
  ) || [];
  const allCategoryItemsWithOrder = [];

  itemsInSameCategory.forEach(categoryItem => {
    if (categoryItem?.has_variants) {
      categoryItem.items?.forEach(vItem => {
        const vPrice = calculateVariantItemPrice(categoryItem, vItem);
        const addOrderHistory = vItem.addOrderHistory || 
          Array.from({ length: vItem.quantity || 0 }, (_, i) => i);
        
        addOrderHistory.forEach(order => {
          allCategoryItemsWithOrder.push({
            price: vPrice,
            itemId: categoryItem.id,
            variantId: vItem.variantId,
            order,
            // Match based on item ID, variant ID, AND price to distinguish items with different add-ons
            isCurrentItem: categoryItem.id === item.id && 
                          (variantItem ? (vItem.variantId === variantItem.variantId && vPrice === unitPrice) : false)
          });
        });
      });
    } else {
      // Use getCateringItemPrice which handles both add_on_options and regular price
      const itemPrice = getCateringItemPrice(categoryItem, currentPax);
      const addOrderHistory = categoryItem.addOrderHistory || 
        Array.from({ length: categoryItem.quantity || 0 }, (_, i) => i);
      
      addOrderHistory.forEach(order => {
        allCategoryItemsWithOrder.push({
          price: itemPrice,
          itemId: categoryItem.id,
          variantId: null,
          order,
          // Match based on item ID AND price to distinguish items with different add-ons
          isCurrentItem: categoryItem.id === item.id && !variantItem && itemPrice === unitPrice
        });
      });
    }
  });

  // Sort by add order (temporal sequence) to maintain the actual addition order
  allCategoryItemsWithOrder.sort((a, b) => a.order - b.order);
  const allCategoryItems = allCategoryItemsWithOrder;

  const maxFreeItemsForCategory = matchingRule.choose;
  const totalCategoryQuantity = allCategoryItems.length;
  
  // If total category quantity is within the free limit, all items are free
  if (totalCategoryQuantity <= maxFreeItemsForCategory) {
    return 0;
  }

  // Count how many instances of the current item are free vs chargeable
  let freeInstancesOfCurrentItem = 0;
  let chargeableInstancesOfCurrentItem = 0;
  
  for (let i = 0; i < allCategoryItems.length; i++) {
    const categoryItem = allCategoryItems[i];
    
    if (categoryItem.isCurrentItem) {
      if (i < maxFreeItemsForCategory) {
        // This instance is within the free limit (cheapest items are free)
        freeInstancesOfCurrentItem++;
      } else {
        // This instance is chargeable (most expensive items are charged)
        chargeableInstancesOfCurrentItem++;
      }
    }
  }

  // Return the price only for chargeable instances
  return chargeableInstancesOfCurrentItem * unitPrice;
};

/**
 * Get selected options string for display
 * @param {Array} selectedVariant - Array of selected variant options
 * @returns {string} Formatted options string
 */
export const getSelectedOptions = (selectedVariant) => {
  return selectedVariant
    .flatMap((variant) =>
      variant.options
        .filter((opt) => opt.option_quantity > 0)
        .map((opt) => `${opt.option_name} (${opt.option_quantity})`)
    )
    .join(", ");
};

/**
 * Transform catering packages data into the API payload format for modify order
 * @param {Object} params - Parameters for the transformation
 * @param {Array} params.packages - Array of catering packages
 * @param {Object} params.filters - Filters containing pax, date, time
 * @param {number} params.deliveryCharge - Delivery charge (default 0)
 * @returns {Object} API payload for modify catering order
 */
export const transformModifyOrderPayload = ({
  packages,
  filters,
  deliveryCharge = 0,
  discountPrice = 0
}) => {
  // Format date to DD-MM-YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-GB');
    
    try {
      // Check if the date is already in DD-MM-YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
        return dateString;
      }
      
      // Handle YYYY-MM-DD format (from date input)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
      }
      
      // Handle other formats by parsing as Date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB');
      }
      
      // If all else fails, return today's date
      console.warn('Unable to format date:', dateString);
      return new Date().toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Error formatting date:', error, 'Date string:', dateString);
      return new Date().toLocaleDateString('en-GB');
    }
  };

  // Transform items to the catering service items format for modify order
  const cateringServiceItems = packages.flatMap(pkg => {
    if (!pkg.items || !Array.isArray(pkg.items)) return [];
    
    // Build a map of items by category to track order and determine which are chargeable
    const itemsByCategory = {};
    
    pkg.items.forEach(item => {
      const categoryId = item.category_id || item.catering_category_id || "1";
      
      if (!itemsByCategory[categoryId]) {
        itemsByCategory[categoryId] = [];
      }
      
      if (item.has_variants && item.items) {
        item.items.forEach(variantItem => {
          const addOrderHistory = variantItem.addOrderHistory || 
            Array.from({ length: variantItem.quantity || 0 }, (_, i) => i);
          
          addOrderHistory.forEach(order => {
            const itemPrice = calculateVariantItemPrice(item, variantItem);
            itemsByCategory[categoryId].push({ order, price: itemPrice });
          });
        });
      } else {
        const addOrderHistory = item.addOrderHistory || 
          Array.from({ length: item.quantity || 0 }, (_, i) => i);
        
        addOrderHistory.forEach(order => {
          const itemPrice = getCateringItemPrice(item, filters.pax);
          itemsByCategory[categoryId].push({ order, price: itemPrice });
        });
      }
    });
    
    // For each category, determine which items are chargeable
    const chargeableMap = {}; // Map to track which items at which order positions are chargeable
    
    Object.keys(itemsByCategory).forEach(categoryId => {
      const categoryItems = itemsByCategory[categoryId];
      const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));
      
      // Separate matrix-based items from standalone/add-on items for modify order
      // We need to check each item in pkg.items to get its price_type
      const matrixBasedOrders = [];
      const standaloneOrAddOnOrders = [];
      
      categoryItems.forEach((categoryItem) => {
        // Find the original item from pkg.items to check its price_type
        const originalItem = pkg.items.find(pkgItem => {
          // Match by checking addOrderHistory contains this order
          if (pkgItem.has_variants && pkgItem.items) {
            return pkgItem.items.some(vItem => 
              (vItem.addOrderHistory || []).includes(categoryItem.order)
            );
          } else {
            return (pkgItem.addOrderHistory || []).includes(categoryItem.order);
          }
        });
        
        if (originalItem) {
          const effectiveAddOnType = originalItem.add_on_type;
          const isStandaloneOrAddOn = effectiveAddOnType || originalItem.price_type === "Standalone Item Based";
          
          if (isStandaloneOrAddOn) {
            standaloneOrAddOnOrders.push(categoryItem.order);
          } else {
            matrixBasedOrders.push(categoryItem);
          }
        } else {
          // If we can't find the original item, assume it's matrix-based (safe default)
          matrixBasedOrders.push(categoryItem);
        }
      });
      
      let maxFreeItems = 0;
      if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
        const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingRule = sortedRules.find(rule => filters.pax >= parseInt(rule.pax));
        
        if (matchingRule && matchingRule.choose > 0) {
          maxFreeItems = matchingRule.choose;
        }
      }
      
      // Sort matrix-based items by order (temporal sequence)
      matrixBasedOrders.sort((a, b) => a.order - b.order);
      
      // For matrix-based items: Items beyond maxFreeItems are chargeable
      const chargeableMatrixOrders = matrixBasedOrders.slice(maxFreeItems).map(item => item.order);
      
      // Combine both sets (all standalone/add-on orders are always chargeable)
      chargeableMap[categoryId] = new Set([...chargeableMatrixOrders, ...standaloneOrAddOnOrders]);
    });
    
    // Group items by ID and add_on_type to send separate objects for each variation
    const groupedItems = {};
    
    pkg.items.forEach(item => {
      const categoryId = item.category_id || item.catering_category_id || "1";
      const itemId = item.id.toString();
      const addOnType = item.add_on_type || ""; // Empty string for normal items
      
      // Create unique key for grouping: itemId + addOnType
      const groupKey = `${itemId}_${addOnType}`;
      
      if (!groupedItems[groupKey]) {
        groupedItems[groupKey] = {
          item: item,
          categoryId: categoryId,
          itemId: itemId,
          addOnType: addOnType,
          quantity: 0,
          addOrderHistory: []
        };
      }
      
      // Accumulate quantity and order history
      if (item.has_variants && item.items) {
        // For variant items
        item.items.forEach(variantItem => {
          groupedItems[groupKey].quantity += variantItem.quantity || 0;
          const variantOrderHistory = variantItem.addOrderHistory || 
            Array.from({ length: variantItem.quantity || 0 }, (_, i) => i);
          groupedItems[groupKey].addOrderHistory.push(...variantOrderHistory);
        });
      } else {
        // For non-variant items
        groupedItems[groupKey].quantity = item.quantity || 0;
        groupedItems[groupKey].addOrderHistory = item.addOrderHistory || 
          Array.from({ length: item.quantity || 0 }, (_, i) => i);
      }
    });
    
    // Now create payload from grouped items
    return Object.values(groupedItems).map(groupedItem => {
      const { item, categoryId, itemId, addOnType, quantity, addOrderHistory } = groupedItem;
      
      // Find the category limit to get the correct "choose" value
      const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));
      let chooseValue = "1"; // Default fallback
      
      if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
        const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingRule = sortedRules.find(rule => 
          filters.pax >= parseInt(rule.pax)
        );
        
        if (matchingRule && matchingRule.choose > 0) {
          chooseValue = matchingRule.choose.toString();
        }
      }
      
      // Determine portion value based on add_on_type or PAX
      const getPortionForPax = (item, currentPax, addOnType) => {
        // PRIORITY 1: If item has add_on_type (half/full), get portion from add_on_options
        if (addOnType && item.add_on_options && Array.isArray(item.add_on_options)) {
          const matchingAddOn = item.add_on_options.find(opt => opt.add_on_type === addOnType);
          if (matchingAddOn && matchingAddOn.portion) {
            return matchingAddOn.portion.toString();
          }
        }
        
        // PRIORITY 2: Otherwise, use portion_by_pax based on PAX
        if (item.portion_by_pax && Array.isArray(item.portion_by_pax) && item.portion_by_pax.length > 0) {
          const sortedPortions = [...item.portion_by_pax].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
          const matchingPortion = sortedPortions.find(p => currentPax >= parseInt(p.pax));
          
          if (matchingPortion && matchingPortion.portions) {
            return matchingPortion.portions.toString();
          }
          
          if (item.portion_by_pax[0] && item.portion_by_pax[0].portions) {
            return item.portion_by_pax[0].portions.toString();
          }
        }
        
        // Default: return "1" as a reasonable fallback
        return "1";
      };
      
      const portionValue = getPortionForPax(item, filters.pax, addOnType);
      
      // Calculate item price (use add_on_type if present)
      // If item is complimentary, set price to 0
      const itemPrice = item.isComplimentary ? 0 : (item.has_variants 
        ? 0 // Will be calculated from variant items
        : getCateringItemPrice(item, filters.pax, addOnType || null));
      
      // Calculate add_on_price: sum of prices for chargeable instances only
      let totalAddOnPrice = 0;
      addOrderHistory.forEach(order => {
        if (chargeableMap[categoryId] && chargeableMap[categoryId].has(order)) {
          totalAddOnPrice += itemPrice;
        }
      });
      
      // Determine if this item is complementary
      // An item is complementary if it has a complimentaryCount > 0
      const isComplementary = (item.complimentaryCount || 0) > 0;
      
      const payloadItem = {
        catering_category_id: categoryId.toString(),
        catering_item_id: itemId,
        quantity: quantity.toString(),
        choose: chooseValue,
        portion: portionValue,
        no_of_pax: filters.pax?.toString() || "20",
        add_on_price: totalAddOnPrice > 0 ? totalAddOnPrice.toFixed(3) : "",
        add_on_type: addOnType,
        currency: "KWD",
        is_complementary: isComplementary
      };
      
      return payloadItem;
    });
  }).flat();

  return {
    service_item_date: formatDate(filters.date),
    service_item_slot: filters.time || "10:30 PM - 11:00 PM",
    discount_price: discountPrice.toFixed(3),
    catering_service_items: cateringServiceItems
  };
};

/**
 * Transform catering packages data into the API payload format for unpaid order update
 * @param {Object} params - Parameters for the transformation
 * @param {Array} params.packages - Array of catering packages
 * @param {Object} params.filters - Filters containing pax, date, time
 * @param {number} params.deliveryCharge - Delivery charge (default 0)
 * @returns {Object} API payload for updating unpaid catering order
 */
export const transformUnpaidOrderPayload = ({
  packages,
  filters,
  deliveryCharge = 0,
  discountPrice = 0
}) => {
  // Calculate total package price
  const totalPackagePrice = packages.reduce((total, pkg) => {
    const packagePrice = calculatePackagePrice(pkg, filters.pax);
    return total + packagePrice;
  }, 0);
  
  // Add chargeable items cost
  const chargeableItemsCost = calculateTotalChargeableItemsCost(packages, filters.pax);
  const totalPriceWithChargeableItems = totalPackagePrice + chargeableItemsCost;
  
  // Format date to DD-MM-YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-GB');
    
    try {
      // Check if the date is already in DD-MM-YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
        return dateString;
      }
      
      // Handle YYYY-MM-DD format (from date input)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
      }
      
      // Handle other formats by parsing as Date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB');
      }
      
      // If all else fails, return today's date
      console.warn('Unable to format date:', dateString);
      return new Date().toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Error formatting date:', error, 'Date string:', dateString);
      return new Date().toLocaleDateString('en-GB');
    }
  };

  // Transform items to the catering service items format for unpaid order
  // Similar to modify order, but simpler - no need to calculate chargeable items
  const cateringServiceItems = packages.flatMap(pkg => {
    if (!pkg.items || !Array.isArray(pkg.items)) return [];
    
    // Group items by ID and add_on_type to send separate objects for each variation
    const groupedItems = {};
    
    pkg.items.forEach(item => {
      const categoryId = item.category_id || item.catering_category_id || "1";
      const itemId = item.id.toString();
      const addOnType = item.add_on_type || ""; // Empty string for normal items
      
      // Create unique key for grouping: itemId + addOnType
      const groupKey = `${itemId}_${addOnType}`;
      
      if (!groupedItems[groupKey]) {
        groupedItems[groupKey] = {
          item: item,
          categoryId: categoryId,
          itemId: itemId,
          addOnType: addOnType,
          quantity: 0
        };
      }
      
      // Accumulate quantity
      if (item.has_variants && item.items) {
        // For variant items
        item.items.forEach(variantItem => {
          groupedItems[groupKey].quantity += variantItem.quantity || 0;
        });
      } else {
        // For non-variant items
        groupedItems[groupKey].quantity = item.quantity || 0;
      }
    });
    
    // Now create payload from grouped items
    return Object.values(groupedItems).map(groupedItem => {
      const { item, categoryId, itemId, addOnType, quantity } = groupedItem;
      
      // Find the category limit to get the correct "choose" value
      const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));
      let chooseValue = "1"; // Default fallback
      
      if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
        const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingRule = sortedRules.find(rule => 
          filters.pax >= parseInt(rule.pax)
        );
        
        if (matchingRule && matchingRule.choose > 0) {
          chooseValue = matchingRule.choose.toString();
        }
      }
      
      // Determine portion value based on add_on_type or PAX
      const getPortionForPax = (item, currentPax, addOnType) => {
        // PRIORITY 1: If item has add_on_type (half/full), get portion from add_on_options
        if (addOnType && item.add_on_options && Array.isArray(item.add_on_options)) {
          const matchingAddOn = item.add_on_options.find(opt => opt.add_on_type === addOnType);
          if (matchingAddOn && matchingAddOn.portion) {
            return matchingAddOn.portion.toString();
          }
        }
        
        // PRIORITY 2: Otherwise, use portion_by_pax based on PAX
        if (item.portion_by_pax && Array.isArray(item.portion_by_pax) && item.portion_by_pax.length > 0) {
          const sortedPortions = [...item.portion_by_pax].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
          const matchingPortion = sortedPortions.find(p => currentPax >= parseInt(p.pax));
          
          if (matchingPortion && matchingPortion.portions) {
            return matchingPortion.portions.toString();
          }
          
          if (item.portion_by_pax[0] && item.portion_by_pax[0].portions) {
            return item.portion_by_pax[0].portions.toString();
          }
        }
        
        // Default: return "1" as a reasonable fallback
        return "1";
      };
      
      const portionValue = getPortionForPax(item, filters.pax, addOnType);
      
      // Calculate add_on_price for items with add_on_type (half/full)
      let addOnPrice = "";
      if (addOnType) {
        // Get the price for this specific add-on type
        const itemPrice = getCateringItemPrice(item, filters.pax, addOnType);
        if (itemPrice > 0) {
          // Multiply by quantity to get total add-on price
          addOnPrice = (itemPrice * quantity).toFixed(3);
        }
      }
      
      // Determine if this item is complementary
      // An item is complementary if it has a complimentaryCount > 0
      const isComplementary = (item.complimentaryCount || 0) > 0;
      
      const payloadItem = {
        catering_category_id: categoryId.toString(),
        catering_item_id: itemId,
        quantity: quantity.toString(),
        choose: chooseValue,
        portion: portionValue,
        add_on_price: addOnPrice,
        add_on_type: addOnType,
        currency: "KWD",
        is_complementary: isComplementary
      };
      
      return payloadItem;
    });
  }).flat();

  // Calculate order_sub_total_price (packages + items, no delivery/discount)
  const orderSubTotalPrice = totalPriceWithChargeableItems;
  // Calculate order_total_price (add delivery, subtract discount)
  const orderTotalPrice = Math.max(0, orderSubTotalPrice + deliveryCharge - discountPrice);

  return {
    service_item_date: formatDate(filters.date),
    service_item_slot: filters.time || "10:30 PM - 11:00 PM",
    order_sub_total_price: orderSubTotalPrice.toFixed(3),
    order_total_price: orderTotalPrice.toFixed(3),
    service_item_price: orderSubTotalPrice.toFixed(3),
    delivery_charge: deliveryCharge.toFixed(3),
    discount_price: discountPrice.toFixed(3),
    catering_service_items: cateringServiceItems
  };
};

/**
 * Transform catering packages data into the combined cart API payload format
 * Used when there are multiple packages (main + additional services)
 * @param {Object} params - Parameters for the transformation
 * @param {number} params.customerId - Customer ID
 * @param {number} params.companyId - Company ID
 * @param {number} params.addressId - Address ID
 * @param {Array} params.packages - Array of catering packages
 * @param {Object} params.mainFilters - Filters for main package (pax, date, time)
 * @param {Object} params.additionalServiceFilters - Filters for additional services (pax)
 * @param {string} params.notes - Special instructions
 * @param {number} params.deliveryCharge - Delivery charges
 * @param {number} params.countryId - Country ID (default 3 for Kuwait)
 * @returns {Object} API payload for combined catering cart
 */
export const transformCombinedCateringCartPayload = ({
  customerId,
  companyId,
  addressId,
  packages,
  mainFilters,
  additionalServiceFilters,
  notes,
  deliveryCharge = 0,
  countryId = 3
}) => {
  if (!packages || packages.length === 0) {
    throw new Error('At least one package is required');
  }

  // Format date to DD-MM-YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-GB');
    
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Transform each package into the combine_catering format
  const combineCatering = packages.map(pkg => {
    // Check if this is a standalone package (Additional Setup)
    const isStandalone = isStandalonePackage(pkg);
    
    // Determine PAX for this package
    // For standalone packages, use 0 PAX (items charge their own price, not PAX-based)
    // For additional services, use the package's own PAX value
    // For main package, use mainFilters PAX
    const pax = isStandalone 
      ? 0
      : (pkg.isAdditionalService 
          ? (pkg.pax || 20)
          : mainFilters.pax);

    console.log(`📦 Package "${pkg.name_en}" - Standalone: ${isStandalone}, PAX: ${pax}`);

    // Calculate package price
    const packageBasePrice = calculatePackagePrice(pkg, pax);
    const packageChargeableItems = calculateTotalChargeableItemsCost([pkg], pax);
    const totalPackagePrice = packageBasePrice + packageChargeableItems;

    // Transform items for this package
    const cateringServiceItems = transformPackageItems(pkg, pax);

    return {
      catering_id: pkg.id,
      restaurant_id: pkg.restaurant_id,
      no_of_pax: pax,
      quantity: 1,
      service_item_price: totalPackagePrice.toFixed(3),
      currency: "KWD",
      service_item_date: formatDate(mainFilters.date),
      service_item_slot: mainFilters.time || "10:30 PM - 11:00 PM",
      is_discount_applied: "",
      promo_code_type: "",
      promo_code: "",
      discount_price: "",
      special_instructions_1: pkg.special_instructions_1 || notes || "",
      special_instructions_2: pkg.special_instructions_2 || "",
      catering_service_items: cateringServiceItems
    };
  });

  // Calculate totals
  const combineSubTotalPrice = packages.reduce((total, pkg) => {
    // Check if this is a standalone package (Additional Setup)
    const isStandalone = isStandalonePackage(pkg);
    
    // Determine PAX for this package
    // For standalone packages, use 0 PAX (items charge their own price, not PAX-based)
    // For additional services, use the package's own PAX value
    // For main package, use mainFilters PAX
    const pax = isStandalone 
      ? 0
      : (pkg.isAdditionalService 
          ? (pkg.pax || 20)
          : mainFilters.pax);
    const packageBasePrice = calculatePackagePrice(pkg, pax);
    const packageChargeableItems = calculateTotalChargeableItemsCost([pkg], pax);
    return total + packageBasePrice + packageChargeableItems;
  }, 0);

  const combineTotalPrice = combineSubTotalPrice + deliveryCharge;

  return {
    country_id: countryId,
    customer_id: customerId,
    company_id: companyId,
    address_id: addressId,
    combine_catering: combineCatering,
    currency: "KWD",
    delivery_charges: deliveryCharge.toFixed(3),
    combine_sub_total_price: combineSubTotalPrice.toFixed(3),
    discount_price: "0.000",
    combine_total_price: combineTotalPrice.toFixed(3),
    combine_notes: notes || ""
  };
};

/**
 * Helper function to check if package contains only standalone items
 * Standalone items don't require PAX-based pricing
 * @param {Object} pkg - Package object
 * @returns {boolean} True if package contains only standalone items
 */
function isStandalonePackage(pkg) {
  if (!pkg) return false;
  
  // Check package name to determine if it's truly a standalone package
  // "Additional Setup" is standalone, but "Additional Services" is matrix-based
  const packageName = pkg.name_en || pkg.title || '';
  const isAdditionalSetup = packageName.toLowerCase().includes('additional setup');
  
  if (!isAdditionalSetup) {
    return false; // Not Additional Setup, treat as matrix-based
  }
  
  // Check BOTH cart items (pkg.items) AND category items (pkg.categories[].catering_service_item)
  // This ensures detection works even when cart is empty
  
  // First check cart items (if any items have been added)
  const hasStandaloneInCart = pkg.items?.some(item => item.price_type === "Standalone Item Based");
  if (hasStandaloneInCart) {
    return true;
  }
  
  // Then check category items (available menu items in package)
  const hasStandaloneInCategories = pkg.categories?.some(category => 
    category.catering_service_item?.some(item => item.price_type === "Standalone Item Based")
  );
  
  return hasStandaloneInCategories || false;
}

/**
 * Helper function to transform package items into catering service items format
 * @param {Object} pkg - Package object
 * @param {number} pax - Number of PAX
 * @returns {Array} Transformed catering service items
 */
function transformPackageItems(pkg, pax) {
  if (!pkg.items || !Array.isArray(pkg.items)) return [];

  // Build a map of items by category
  const itemsByCategory = {};
  const itemDetails = [];

  pkg.items.forEach(item => {
    const categoryId = item.category_id || item.catering_category_id || "1";

    if (!itemsByCategory[categoryId]) {
      itemsByCategory[categoryId] = [];
    }

    if (item.has_variants && item.items) {
      item.items.forEach(variantItem => {
        const addOrderHistory = variantItem.addOrderHistory || 
          Array.from({ length: variantItem.quantity || 0 }, (_, i) => i);

        addOrderHistory.forEach(order => {
          const itemPrice = calculateVariantItemPrice(item, variantItem);
          itemsByCategory[categoryId].push({ order, price: itemPrice });
          itemDetails.push({
            categoryId,
            item,
            variantItem,
            order,
            price: itemPrice,
            isVariant: true
          });
        });
      });
    } else {
      const addOrderHistory = item.addOrderHistory || 
        Array.from({ length: item.quantity || 0 }, (_, i) => i);

      addOrderHistory.forEach(order => {
        const itemPrice = getCateringItemPrice(item, pax, item.add_on_type);
        itemsByCategory[categoryId].push({ order, price: itemPrice });
        itemDetails.push({
          categoryId,
          item,
          order,
          price: itemPrice,
          isVariant: false
        });
      });
    }
  });

  // Determine chargeable items per category
  const chargeableMap = {};

  Object.keys(itemsByCategory).forEach(categoryId => {
    const categoryItems = itemsByCategory[categoryId];
    const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));

    let maxFreeItemsForCategory = 0;

    if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
      const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
      const matchingRule = sortedRules.find(rule => pax >= parseInt(rule.pax));

      if (matchingRule && matchingRule.choose > 0) {
        maxFreeItemsForCategory = matchingRule.choose;
      }
    }

    const sortedItems = [...categoryItems].sort((a, b) => a.order - b.order);
    const totalCategoryItems = sortedItems.length;
    const chargeableCount = Math.max(0, totalCategoryItems - maxFreeItemsForCategory);

    const chargeableOrders = sortedItems
      .slice(maxFreeItemsForCategory)
      .map(item => item.order);

    chargeableMap[categoryId] = new Set(chargeableOrders);
  });

  // Group items by category and item ID
  const groupedItems = {};

  itemDetails.forEach(detail => {
    const key = `${detail.categoryId}_${detail.item.id}_${detail.item.add_on_type || ''}`;

    if (!groupedItems[key]) {
      // Calculate portion value
      let portionValue = "1"; // Default fallback
      
      // PRIORITY 1: If item has add_on_type (half/full), get portion from add_on_options
      if (detail.item.add_on_type && detail.item.add_on_options && Array.isArray(detail.item.add_on_options)) {
        const matchingAddOn = detail.item.add_on_options.find(opt => opt.add_on_type === detail.item.add_on_type);
        if (matchingAddOn && matchingAddOn.portion) {
          portionValue = matchingAddOn.portion.toString();
        }
      } 
      // PRIORITY 2: Otherwise, use portion_by_pax based on PAX
      else if (detail.item.portion_by_pax && Array.isArray(detail.item.portion_by_pax) && detail.item.portion_by_pax.length > 0) {
        const sortedPortions = [...detail.item.portion_by_pax].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingPortion = sortedPortions.find(p => pax >= parseInt(p.pax));
        
        if (matchingPortion && matchingPortion.portions) {
          portionValue = matchingPortion.portions.toString();
        } else if (detail.item.portion_by_pax[0] && detail.item.portion_by_pax[0].portions) {
          portionValue = detail.item.portion_by_pax[0].portions.toString();
        }
      }
      
      groupedItems[key] = {
        catering_category_id: detail.categoryId.toString(),
        catering_item_id: detail.item.id.toString(),
        quantity: 0,
        choose: 0,
        portion: portionValue,
        add_on_price: "0.000",
        add_on_type: detail.item.add_on_type || "",
        currency: "KWD",
        orders: [],
        item: detail.item, // Store item reference for later use
        // Track complementary status - will be determined from the original item
        is_complementary: (detail.item.complimentaryCount || 0) > 0
      };
    }

    groupedItems[key].quantity += 1;
    groupedItems[key].orders.push(detail.order);
  });

  // Calculate add_on_price and choose
  Object.keys(groupedItems).forEach(key => {
    const group = groupedItems[key];
    const categoryId = group.catering_category_id;
    const chargeableSet = chargeableMap[categoryId] || new Set();

    const chargeableOrders = group.orders.filter(order => chargeableSet.has(order));
    
    // Calculate "choose" - should be the category limit for this PAX, not chargeable count
    // Find the category limit to get the correct "choose" value
    const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));
    let chooseValue = "1"; // Default fallback
    
    if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
      const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
      const matchingRule = sortedRules.find(rule => pax >= parseInt(rule.pax));
      
      if (matchingRule && matchingRule.choose > 0) {
        chooseValue = matchingRule.choose.toString();
      }
    }
    
    group.choose = chooseValue;

    // Calculate add_on_price
    let addOnPrice = 0;
    chargeableOrders.forEach(order => {
      const detail = itemDetails.find(
        d => d.categoryId.toString() === categoryId &&
             d.item.id.toString() === group.catering_item_id &&
             d.order === order &&
             (d.item.add_on_type || "") === group.add_on_type
      );
      if (detail) {
        addOnPrice += detail.price;
      }
    });

    group.add_on_price = addOnPrice.toFixed(3);
    delete group.orders; // Remove temporary field
    delete group.item; // Remove item reference
  });

  return Object.values(groupedItems);
};

/**
 * Transform combined order packages into the API payload format for paid order modification
 * @param {Object} params - Parameters for the transformation
 * @param {Array} params.packages - Array of catering packages (from combined order)
 * @param {Object} params.filters - Global filters containing pax, date, time
 * @param {number} params.deliveryCharge - Delivery charge (default 0)
 * @param {number} params.combineOrderId - The combine order ID
 * @returns {Object} API payload for modifying combined paid catering order
 */
export const transformCombinedModifyOrderPayload = ({
  packages,
  filters,
  deliveryCharge = 0,
  discountPrice = 0,
  combineOrderId = 1
}) => {
  // Format date to DD-MM-YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-GB');
    
    try {
      // Check if the date is already in DD-MM-YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
        return dateString;
      }
      
      // Handle YYYY-MM-DD format (from date input)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
      }
      
      // Handle other formats by parsing as Date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB');
      }
      
      // If all else fails, return today's date
      console.warn('Unable to format date:', dateString);
      return new Date().toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Error formatting date:', error, 'Date string:', dateString);
      return new Date().toLocaleDateString('en-GB');
    }
  };

  // Transform each package into a combine_orders entry
  const combineOrders = packages.map(pkg => {
    // Check if this is a standalone package (Additional Setup)
    const isStandalone = isStandalonePackage(pkg);
    
    // Get the PAX for this package
    // For standalone packages, use 0 PAX (items charge their own price, not PAX-based)
    // For additional services, use package's own PAX
    // For main package, use filters PAX
    const pax = isStandalone 
      ? 0 
      : (pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax);
    
    console.log(`📦 Modify Payload - Package "${pkg.name_en}" - Standalone: ${isStandalone}, PAX: ${pax}`);
    
    // Transform items to the catering service items format
    const cateringServiceItems = transformModifyOrderPackageItems(pkg, pax);

    // Get date and time for this package (each package can have different dates/times in combined orders)
    const packageDate = pkg.service_item_date || filters.date;
    const packageTime = pkg.service_item_slot || filters.time || "10:30 PM - 11:00 PM";

    return {
      catering_order_id: pkg.originalOrderId || pkg.id, // Use originalOrderId if available from order restoration
      service_item_date: formatDate(packageDate),
      service_item_slot: packageTime,
      catering_service_items: cateringServiceItems
    };
  });

  return {
    combine_order_id: combineOrderId,
    discount_price: discountPrice.toFixed(3),
    combine_orders: combineOrders
  };
};

/**
 * Transform combined order packages into the API payload format for unpaid order modification
 * @param {Object} params - Parameters for the transformation
 * @param {Array} params.packages - Array of catering packages (from combined order)
 * @param {Object} params.filters - Global filters containing pax, date, time
 * @param {number} params.deliveryCharge - Delivery charge (default 0)
 * @param {number} params.combineOrderId - The combine order ID
 * @returns {Object} API payload for modifying combined unpaid catering order
 */
export const transformCombinedUnpaidOrderPayload = ({
  packages,
  filters,
  deliveryCharge = 0,
  discountPrice = 0,
  combineOrderId = 1
}) => {
  // Format date to DD-MM-YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-GB');
    
    try {
      // Check if the date is already in DD-MM-YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
        return dateString;
      }
      
      // Handle YYYY-MM-DD format (from date input)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
      }
      
      // Handle other formats by parsing as Date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB');
      }
      
      // If all else fails, return today's date
      console.warn('Unable to format date:', dateString);
      return new Date().toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Error formatting date:', error, 'Date string:', dateString);
      return new Date().toLocaleDateString('en-GB');
    }
  };

  // Transform each package into a combine_orders entry
  const combineOrders = packages.map(pkg => {
    // Check if this is a standalone package (Additional Setup)
    const isStandalone = isStandalonePackage(pkg);
    
    // Get the PAX for this package
    // For standalone packages, use 0 PAX (items charge their own price, not PAX-based)
    // For additional services, use package's own PAX
    // For main package, use filters PAX
    const pax = isStandalone 
      ? 0 
      : (pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax);
    
    console.log(`📦 Unpaid Payload - Package "${pkg.name_en}" - Standalone: ${isStandalone}, PAX: ${pax}`);
    
    // Transform items to the catering service items format
    const cateringServiceItems = transformModifyOrderPackageItems(pkg, pax);

    // Get date and time for this package (each package can have different dates/times in combined orders)
    const packageDate = pkg.service_item_date || filters.date;
    const packageTime = pkg.service_item_slot || filters.time || "10:30 PM - 11:00 PM";

    return {
      catering_order_id: pkg.originalOrderId || pkg.id, // Use originalOrderId if available from order restoration
      service_item_date: formatDate(packageDate),
      service_item_slot: packageTime,
      catering_service_items: cateringServiceItems
    };
  });

  return {
    combine_order_id: combineOrderId,
    discount_price: discountPrice.toFixed(3),
    combine_orders: combineOrders
  };
};

/**
 * Helper function to transform package items for modify order (used by both combined and single orders)
 * @param {Object} pkg - Package object
 * @param {number} pax - Number of PAX
 * @returns {Array} Transformed catering service items for modify order
 */
function transformModifyOrderPackageItems(pkg, pax) {
  if (!pkg.items || !Array.isArray(pkg.items)) return [];

  // Build a map of items grouped by category, item ID, and add_on_type
  const itemsByCategory = {};
  const itemDetails = [];

  pkg.items.forEach(item => {
    const categoryId = item.category_id || item.catering_category_id || "1";

    if (!itemsByCategory[categoryId]) {
      itemsByCategory[categoryId] = [];
    }

    if (item.has_variants && item.items) {
      // Handle variant items
      item.items.forEach(variantItem => {
        const addOrderHistory = variantItem.addOrderHistory || 
          Array.from({ length: variantItem.quantity || 0 }, (_, i) => i);

        addOrderHistory.forEach(order => {
          const itemPrice = calculateVariantItemPrice(item, variantItem);
          itemsByCategory[categoryId].push({ 
            order, 
            price: itemPrice,
            addOnType: item.add_on_type || ""
          });
          itemDetails.push({
            categoryId,
            itemId: item.id,
            item,
            variantItem,
            order,
            price: itemPrice,
            isVariant: true,
            addOnType: item.add_on_type || "",
            complimentaryCount: item.complimentaryCount || 0
          });
        });
      });
    } else {
      // Handle non-variant items
      const addOrderHistory = item.addOrderHistory || 
        Array.from({ length: item.quantity || 0 }, (_, i) => i);

      addOrderHistory.forEach(order => {
        const itemPrice = getCateringItemPrice(item, pax, item.add_on_type);
        itemsByCategory[categoryId].push({ 
          order, 
          price: itemPrice,
          addOnType: item.add_on_type || ""
        });
        itemDetails.push({
          categoryId,
          itemId: item.id,
          item,
          order,
          price: itemPrice,
          isVariant: false,
          addOnType: item.add_on_type || "",
          complimentaryCount: item.complimentaryCount || 0
        });
      });
    }
  });

  // Determine chargeable items per category based on choose_rule
  const chargeableMap = {};

  Object.keys(itemsByCategory).forEach(categoryId => {
    const categoryItems = itemsByCategory[categoryId];
    const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));

    // Separate matrix-based items from standalone/add-on items
    const matrixBasedOrders = [];
    const standaloneOrAddOnOrders = [];
    
    categoryItems.forEach((categoryItem) => {
      // Find the original item from itemDetails to check its properties
      const originalItemDetail = itemDetails.find(detail => 
        detail.order === categoryItem.order && 
        detail.categoryId === categoryId
      );
      
      if (originalItemDetail) {
        const effectiveAddOnType = originalItemDetail.addOnType;
        const isStandaloneOrAddOn = effectiveAddOnType || 
          originalItemDetail.item.price_type === "Standalone Item Based";
        
        if (isStandaloneOrAddOn) {
          standaloneOrAddOnOrders.push(categoryItem.order);
        } else {
          matrixBasedOrders.push(categoryItem);
        }
      } else {
        // If we can't find the original item, assume it's matrix-based (safe default)
        matrixBasedOrders.push(categoryItem);
      }
    });
    
    let maxFreeItems = 0;
    if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
      const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
      const matchingRule = sortedRules.find(rule => pax >= parseInt(rule.pax));

      if (matchingRule && matchingRule.choose > 0) {
        maxFreeItems = matchingRule.choose;
      }
    }

    // Sort matrix-based items by order and mark chargeable ones
    const sortedMatrixItems = [...matrixBasedOrders].sort((a, b) => a.order - b.order);
    const chargeableMatrixCount = Math.max(0, sortedMatrixItems.length - maxFreeItems);
    const chargeableMatrixOrders = sortedMatrixItems
      .slice(maxFreeItems)
      .map(item => item.order);

    // All standalone/add-on items are chargeable
    chargeableMap[categoryId] = new Set([...chargeableMatrixOrders, ...standaloneOrAddOnOrders]);
  });

  // Group items by category, item ID, and add_on_type
  const groupedItems = {};

  itemDetails.forEach(detail => {
    const addOnType = detail.addOnType || "";
    const key = `${detail.categoryId}_${detail.itemId}_${addOnType}`;

    if (!groupedItems[key]) {
      groupedItems[key] = {
        item: detail.item,
        categoryId: detail.categoryId,
        itemId: detail.itemId,
        quantity: 0,
        addOrderHistory: [],
        addOnType: addOnType,
        complimentaryCount: detail.complimentaryCount
      };
    }

    groupedItems[key].quantity += 1;
    groupedItems[key].addOrderHistory.push(detail.order);
  });

  // Now create payload from grouped items
  return Object.values(groupedItems).map(groupedItem => {
    const { item, categoryId, itemId, quantity, addOrderHistory, addOnType, complimentaryCount } = groupedItem;
    
    // Find the category limit to get the correct "choose" value
    const categoryLimit = pkg.categories?.find(category => category.id === parseInt(categoryId));
    let chooseValue = "1"; // Default fallback
    
    if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
      const sortedRules = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
      const matchingRule = sortedRules.find(rule => 
        pax >= parseInt(rule.pax)
      );
      
      if (matchingRule && matchingRule.choose > 0) {
        chooseValue = matchingRule.choose.toString();
      }
    }
    
    // Determine portion value based on add_on_type or PAX
    const getPortionForPax = (item, currentPax, addOnType) => {
      // PRIORITY 1: If item has add_on_type (half/full), get portion from add_on_options
      if (addOnType && item.add_on_options && Array.isArray(item.add_on_options)) {
        const matchingAddOn = item.add_on_options.find(opt => opt.add_on_type === addOnType);
        if (matchingAddOn && matchingAddOn.portion) {
          return matchingAddOn.portion.toString();
        }
      }
      
      // PRIORITY 2: Otherwise, use portion_by_pax based on PAX
      if (item.portion_by_pax && Array.isArray(item.portion_by_pax) && item.portion_by_pax.length > 0) {
        const sortedPortions = [...item.portion_by_pax].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        const matchingPortion = sortedPortions.find(p => currentPax >= parseInt(p.pax));
        
        if (matchingPortion && matchingPortion.portions) {
          return matchingPortion.portions.toString();
        }
        
        if (item.portion_by_pax[0] && item.portion_by_pax[0].portions) {
          return item.portion_by_pax[0].portions.toString();
        }
      }
      
      // Default: return "1" as a reasonable fallback
      return "1";
    };
    
    const portionValue = getPortionForPax(item, pax, addOnType);

    // Calculate unit price for this item
    const itemPrice = item.has_variants 
      ? 0 // Will be calculated from variant items
      : getCateringItemPrice(item, pax, addOnType || null);
    
    // Calculate add_on_price: sum of prices for chargeable instances only
    let totalAddOnPrice = 0;
    addOrderHistory.forEach(order => {
      if (chargeableMap[categoryId] && chargeableMap[categoryId].has(order)) {
        totalAddOnPrice += itemPrice;
      }
    });
    
    // Determine if this item is complementary
    const isComplementary = (complimentaryCount || 0) > 0;
    
    const payloadItem = {
      catering_category_id: categoryId.toString(),
      catering_item_id: itemId.toString(),
      quantity: quantity.toString(),
      choose: chooseValue,
      portion: portionValue,
      no_of_pax: pax?.toString() || "20",
      add_on_price: totalAddOnPrice > 0 ? totalAddOnPrice.toFixed(3) : "",
      add_on_type: addOnType,
      currency: "KWD",
      is_complementary: isComplementary
    };
    
    return payloadItem;
  });
};

/**
 * Transform payload for reordering paid cancelled catering orders
 * @param {Object} params - Transformation parameters
 * @param {Array} params.packages - Array of packages (usually one package)
 * @param {Object} params.filters - Filter object with pax, date, time
 * @param {number} params.deliveryCharge - Delivery charge amount
 * @param {number} params.originalTotal - Original total from cancelled order
 * @param {number} params.currentTotal - Current total after modifications
 * @param {string} params.comments - Comments/notes for the reorder
 * @returns {Object} Transformed payload for reorder API
 */
export const transformReorderPayload = ({
  packages,
  filters,
  deliveryCharge = 0,
  discountPrice = 0,
  originalTotal = 0,
  currentTotal = 0,
  comments = ""
}) => {
  // Format date to DD-MM-YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-GB');
    
    try {
      // Check if the date is already in DD-MM-YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
        return dateString;
      }
      
      // Handle YYYY-MM-DD format (from date input)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
      }
      
      // Handle other formats by parsing as Date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB');
      }
      
      // If all else fails, return today's date
      console.warn('Unable to format date:', dateString);
      return new Date().toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Error formatting date:', error, 'Date string:', dateString);
      return new Date().toLocaleDateString('en-GB');
    }
  };

  // Get the main package (first one)
  const pkg = packages[0];
  const pax = filters.pax || 20;
  
  // Calculate service item price (subtotal without delivery charge or discount)
  const serviceItemPrice = currentTotal - deliveryCharge + discountPrice;
  
  // Calculate order_sub_total_price (packages + items, no delivery/discount)
  const orderSubTotalPrice = serviceItemPrice;
  
  // Calculate order_total_price (add delivery, subtract discount)
  const orderTotalPrice = currentTotal;
  
  // Calculate balance amount (difference from original total)
  const balanceAmount = currentTotal - originalTotal;
  
  // Determine reorder type: upgrade or downgrade
  const reorderType = balanceAmount > 0 ? "upgrade" : (balanceAmount < 0 ? "downgrade" : "");
  
  // Transform items to the catering service items format
  const cateringServiceItems = transformModifyOrderPackageItems(pkg, pax);
  
  return {
    service_item_date: formatDate(filters.date),
    service_item_slot: filters.time || "10:30 PM - 11:00 PM",
    no_of_pax: pax,
    service_item_price: serviceItemPrice.toFixed(3),
    currency: "KWD",
    order_sub_total_price: orderSubTotalPrice.toFixed(3),
    order_total_price: orderTotalPrice.toFixed(3),
    balance_amount: Math.abs(balanceAmount).toFixed(3),
    reorder_type: reorderType,
    promo_code_type: "",
    promo_code: "",
    discount_price: discountPrice.toFixed(3),
    comments: comments || "",
    catering_service_items: cateringServiceItems
  };
};

/**
 * Transform payload for reordering combined paid cancelled catering orders
 * @param {Object} params - Transformation parameters
 * @param {Array} params.packages - Array of packages (combined order)
 * @param {Object} params.filters - Filter object with pax, date, time
 * @param {number} params.deliveryCharge - Delivery charge amount
 * @param {number} params.originalTotal - Original total from cancelled order
 * @param {number} params.currentTotal - Current total after modifications
 * @param {number} params.combineOrderId - Combine order ID from past order
 * @param {string} params.comments - Comments/notes for the reorder
 * @returns {Object} Transformed payload for combined reorder API
 */
export const transformCombinedReorderPayload = ({
  packages,
  filters,
  deliveryCharge = 0,
  discountPrice = 0,
  originalTotal = 0,
  currentTotal = 0,
  combineOrderId = null,
  comments = ""
}) => {
  // Format date to DD-MM-YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-GB');
    
    try {
      // Check if the date is already in DD-MM-YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
        return dateString;
      }
      
      // Handle YYYY-MM-DD format (from date input)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
      }
      
      // Handle other formats by parsing as Date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB');
      }
      
      // If all else fails, return today's date
      console.warn('Unable to format date:', dateString);
      return new Date().toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Error formatting date:', error, 'Date string:', dateString);
      return new Date().toLocaleDateString('en-GB');
    }
  };

  // Calculate service item price (subtotal without delivery charge or discount)
  const serviceItemPrice = currentTotal - deliveryCharge + discountPrice;
  
  // Calculate order_sub_total_price (packages + items, no delivery/discount)
  const orderSubTotalPrice = serviceItemPrice;
  
  // Calculate order_total_price (add delivery, subtract discount)
  const orderTotalPrice = currentTotal;
  
  // Calculate balance amount (difference from original total)
  const balanceAmount = currentTotal - originalTotal;
  
  // Determine reorder type: upgrade or downgrade
  const reorderType = balanceAmount > 0 ? "upgrade" : (balanceAmount < 0 ? "downgrade" : "");
  
  // Transform each package into combine_orders format
  const combineOrders = packages.map(pkg => {
    // Check if this is a standalone package (Additional Setup)
    const isStandalone = isStandalonePackage(pkg);
    
    // Determine PAX for this package
    // For standalone packages, use 0 PAX (items charge their own price, not PAX-based)
    // For additional services, use package's own PAX
    // For main package, use filters PAX
    const paxToUse = isStandalone 
      ? 0 
      : (pkg.isAdditionalService ? (pkg.pax || 20) : filters.pax);
    
    console.log(`📦 Reorder Payload - Package "${pkg.name_en}" - Standalone: ${isStandalone}, PAX: ${paxToUse}`);
    
    // Calculate package price
    const packagePrice = calculatePackagePrice(pkg, paxToUse);
    
    // Transform items to the catering service items format
    const cateringServiceItems = transformModifyOrderPackageItems(pkg, paxToUse);
    
    // Use stored date/time for this package, or fall back to filters
    const pkgDate = pkg.service_item_date || filters.date;
    const pkgTime = pkg.service_item_slot || filters.time || "10:30 PM - 11:00 PM";
    
    return {
      catering_order_id: pkg.originalOrderId || pkg.id, // Use originalOrderId if available from past order
      service_item_date: formatDate(pkgDate),
      service_item_slot: pkgTime,
      no_of_pax: paxToUse,
      service_item_price: packagePrice.toFixed(3),
      comments: comments || "",
      catering_service_items: cateringServiceItems
    };
  });
  
  return {
    combine_order_id: combineOrderId,
    order_sub_total_price: orderSubTotalPrice.toFixed(3),
    order_total_price: orderTotalPrice.toFixed(3),
    balance_amount: Math.abs(balanceAmount).toFixed(3),
    reorder_type: reorderType,
    currency: "KWD",
    promo_code_type: "",
    promo_code: "",
    discount_price: discountPrice.toFixed(3),
    combine_orders: combineOrders
  };
};